import axios from '@fs/zion-axios'
import { useCallback, useMemo, useState } from 'react'
import { memoize } from 'lodash-es'
import useFetchData from './useFetchData'
import { ContactIdAlreadyInUseError } from '../errors'
import throwUserProfileError from '../services/throwUserProfileError'

const profileUrl = '/service/user/profile/users/CURRENT'
const versionHeader = 'application/familysearch-user-profile+v1.1+json'

let profileCacheKey = 0

const fetchProfile = memoize(
  async () => {
    const { data } = await axios.get(profileUrl, {
      headers: {
        accept: versionHeader,
      },
    })
    return data
  },
  () => profileCacheKey
)

// exported so testUtils can cache bust between tests
export function invalidateProfileFetchCache() {
  fetchProfile.cache.clear()
  profileCacheKey++
}

export default function useProfile() {
  const [refreshToken, setRefreshToken] = useState(0)
  const { data: remoteProfile, loading: profileLoading } = useFetchData(fetchProfile, { requestFnArg: refreshToken })
  const [localProfile, setLocalProfile] = useState(null)
  const profile = useMemo(() => ({ ...remoteProfile, ...localProfile }), [remoteProfile, localProfile])

  const invalidateProfileCache = useCallback(() => {
    fetchProfile.cache.clear()
    profileCacheKey++
    setRefreshToken((t) => t + 1)
    setLocalProfile(null)
  }, [])
  const updateLocalProfile = useCallback((partial) => setLocalProfile((prev) => ({ ...prev, ...partial })), [])

  const handleChangeProfile = useCallback(async (newProfile) => {
    try {
      const response = await axios.post(profileUrl, newProfile, {
        headers: { 'Content-Type': 'application/json', accept: versionHeader },
      })
      setLocalProfile({ ...response.data })
    } catch (err) {
      if (err?.response?.data?.message?.includes('contactId is not available')) {
        throw new ContactIdAlreadyInUseError()
      }
      // Add User Profile API error handling
      throwUserProfileError(err)
    }
  }, [])

  return {
    profile,
    // optimistically render if we can (so loading is only for initial fetch)
    profileLoading: refreshToken === 0 ? profileLoading : false,
    handleChangeProfile,
    invalidateProfileCache,
    updateLocalProfile,
  }
}
